import { ChangeEvent, useState } from "react";

import { Button, ModalDialog } from "@ezmid/components-library";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import * as yup from "yup";

import { LessonContentForm } from "./LessonContentForm";
import {
  useCreateLessonContentMutation,
  EntityLessonContentStatus,
} from "../../../api/lessons-create-content.generated";
import { useDeleteBackOfficeMediaMutation } from "../../../api/media.generated";
import { Optional, ParamId } from "../../../enums/types";
import { useUpload, useUploadMedia } from "../../../hooks";
import { useGetMedia } from "../../../hooks/useGetMedia";
import { handleValidationErrors } from "../../../utils/validationErrorHandler";
import { buildTranslations } from "../utils/lessonContentUtils";

type FormValues = {
  lessonContentType: string;
  nameEN: string;
  nameDE: string;
  textEN: string;
  textDE: string;
  order: string;
  status: EntityLessonContentStatus;
  notePlaceholder?: string;
  scaleContent?: string;
  scaleMinValue?: string;
  scaleMinLabelDE?: string;
  scaleMinLabelEN?: string;
  scaleMaxValue?: string;
  scaleMaxLabelDE?: string;
  scaleMaxLabelEN?: string;
};

type Props = {
  onModalClose: () => void;
  refetch: () => void;
  isModalOpen: boolean;
};

export function AddLessonContentModal({
  onModalClose,
  refetch,
  isModalOpen,
}: Props) {
  const { t } = useTranslation();
  const { id }: ParamId = useParams();
  const [addLessonContent] = useCreateLessonContentMutation();
  const [deleteMedia] = useDeleteBackOfficeMediaMutation();
  const { addFileToFormData } = useUpload();
  const [isDirtyFromImage, setIsDirtyFromImage] = useState<boolean>(false);

  const FormSchema = yup.object().shape({
    lessonContentType: yup.string().required("Content type is required"),
    order: yup.string().required(t("form.required")),
    nameEN: yup.string().required(t("form.name.required")),
    textEN: yup.string().required(t("form.description")),
  });

  const defaultFormValues: FormValues = {
    lessonContentType: "lct-theory",
    order: "",
    nameEN: "",
    nameDE: "",
    textEN: "",
    textDE: "",
    status: "lcs-draft",
    notePlaceholder: "",
    scaleContent: "",
    scaleMinValue: "",
    scaleMinLabelDE: "",
    scaleMinLabelEN: "",
    scaleMaxValue: "",
    scaleMaxLabelDE: "",
    scaleMaxLabelEN: "",
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    getValues,
    setError,
    reset,
  } = useForm<FormValues>({
    defaultValues: defaultFormValues,
    mode: "all",
    resolver: yupResolver(FormSchema),
  });

  const handleClose = () => {
    reset(defaultFormValues);
    setMediaFormData(null);
    setSelectedFileName(null);
    setIsMediaDeleted(false);
    setIsDirtyFromImage(false);
    onModalClose();
  };

  const [mediaFormData, setMediaFormData] = useState<Optional<FormData>>(null);
  const [selectedFileName, setSelectedFileName] =
    useState<Optional<string>>(null);
  const [isMediaDeleted, setIsMediaDeleted] = useState<boolean>(false);
  const { uploadedMediaId, isMediaUploading } = useUploadMedia(mediaFormData);
  const { downloadedMedia, isMediaDownloading } = useGetMedia(
    !isMediaDeleted ? uploadedMediaId : undefined
  );

  const onDeleteMedia = () => {
    if (uploadedMediaId) {
      deleteMedia({ id: uploadedMediaId })
        .unwrap()
        .then(() => {
          toast.success(t("app.media.delete.success"));
        })
        .catch(() => {
          toast.warning(t("app.media.delete.failed"));
        });
    }
    setMediaFormData(null);
    setSelectedFileName(null);
    setIsMediaDeleted(true);
    setIsDirtyFromImage(true);
  };

  const onSubmit = () => {
    const values = getValues();
    setIsDirtyFromImage(false);

    const [enTranslation, deTranslation] = buildTranslations(values);

    addLessonContent({
      id: id || "",
      lessonContentUpdateRequest: {
        lessonContentType: values.lessonContentType as any,
        order: parseInt(values.order, 10),
        translations: [enTranslation, deTranslation],
        mediaId: uploadedMediaId || undefined,
        status: values.status,
      },
    })
      .unwrap()
      .then(() => {
        handleClose();
        refetch();
        toast.success(t("course.create.lesson.content.success"));
      })
      .catch((error) => {
        const { hasFieldErrors, errorMessage } = handleValidationErrors(
          error,
          setError
        );
        if (!hasFieldErrors) {
          toast.warning(
            errorMessage || t("course.create.lesson.content.failed")
          );
        }
      });
  };

  const onChangeMedia = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const file = e.target.files[0];
      setMediaFormData(addFileToFormData(file));
      setSelectedFileName(file.name);
      setIsDirtyFromImage(true);
      setIsMediaDeleted(false);
    }
  };

  const isFormDirty = isDirty || isDirtyFromImage;
  const isSubmitButtonDisabled = !isFormDirty || isMediaUploading;

  return (
    <ModalDialog
      id="lesson-content-create"
      title={t("course.lesson.create.content")}
      isOpen={isModalOpen}
      overlayStyles={{
        zIndex: 1000,
        overflow: "visible",
      }}
      dialogStyles={{
        zIndex: 1000,
        overflow: "auto",
        maxHeight: "90vh",
      }}
      onRequestClose={handleClose}
      footerControls={
        <>
          <Button type="button" onClick={handleClose}>
            {t("button.close")}
          </Button>
          <div className="px-2">
            <Button onClick={onSubmit} disabled={isSubmitButtonDisabled}>
              {t("button.save")}
            </Button>
          </div>
        </>
      }
    >
      <LessonContentForm
        control={control}
        errors={errors}
        onMediaFileChange={onChangeMedia}
        handleSubmit={handleSubmit(onSubmit)}
        uploadedMedia={!isMediaDeleted ? downloadedMedia : undefined}
        isMediaUploading={isMediaUploading || isMediaDownloading}
        selectedFileName={selectedFileName || undefined}
        onMediaDelete={onDeleteMedia}
      />
    </ModalDialog>
  );
}
